import React from 'react';
import { Text } from 'src/modules/shared/components/atoms/text';

/** A message that other controls reference with `aria-describedby`. */
interface DescribedMessage {
  /** Element id the controls point at. */
  id: string;
  /** Message text. */
  text: string;
}

/** Props for the logo upload feedback. */
interface WebsiteLogoFeedbackProps {
  /** Notice shown while the selected design ignores logos. */
  inactive?: DescribedMessage;
  /** Upload progress, while an upload is running. */
  progress?: {
    /** Progress from zero through one hundred. */
    value: number;
    /** Accessible name of the progress bar. */
    label: string;
  };
  /** Polite status announcement for the current operation. */
  statusMessage?: string;
  /** Notice that the published logo is cleaned up after the next publication. */
  deferredCleanup?: DescribedMessage;
  /** Validation or upload error. */
  error?: DescribedMessage;
}

/**
 * Feedback under the logo picker: the inactive-design notice, upload
 * progress, status announcements and errors. Each part renders only when it
 * has something to say.
 *
 * @param props - The messages and progress to show.
 * @returns The feedback, or nothing.
 */
export const WebsiteLogoFeedback: React.FC<WebsiteLogoFeedbackProps> = ({
  inactive,
  progress,
  statusMessage,
  deferredCleanup,
  error,
}) => (
  <>
    {inactive ? (
      <Text id={inactive.id} size="tiny" color="warning">
        {inactive.text}
      </Text>
    ) : null}
    {progress ? (
      <div
        className="h-2 overflow-hidden rounded-full bg-neutral-200"
        role="progressbar"
        aria-label={progress.label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progress.value}
      >
        <div
          className="h-full bg-info-500 transition-[width]"
          style={{ width: `${progress.value}%` }}
        />
      </div>
    ) : null}
    {statusMessage || deferredCleanup ? (
      <div role="status" aria-live="polite" className="space-y-1">
        {statusMessage ? (
          <Text size="tiny" color="secondary">
            {statusMessage}
          </Text>
        ) : null}
        {deferredCleanup ? (
          <Text id={deferredCleanup.id} size="tiny" color="warning">
            {deferredCleanup.text}
          </Text>
        ) : null}
      </div>
    ) : null}
    {error ? (
      <div role="alert">
        <Text id={error.id} size="tiny" color="danger">
          {error.text}
        </Text>
      </div>
    ) : null}
  </>
);
